import { Causes } from '@platform/client';
import { ProfessionalApplicationParameters } from '@platform/client/schemas';
import { MultiSelectDropdown } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/SDGvisual.svg';

type FormValues = {
  impactAreas: string[];
};

export const ImpactAreaSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };
  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<ProfessionalApplicationParameters>(dataKey);

  const sdgs = Causes.useGetCauses();

  const sdgOptions =
    sdgs.isSuccess ?
      sdgs.data.map((sdg) => ({
        value: sdg.name,
        label: sdg.shortName,
        description: sdg.shortDescription,
      }))
    : [];

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    register,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { impactAreas: getApplicationDataByKey('impactAreas') || [] },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({ impactAreas: data.impactAreas });
    onContinue();
  };

  const onSelectionChange = (value: string[]) => {
    setValue('impactAreas', value);
  };

  const handleBack = () => {
    updateApplicationData({
      impactAreas: watch('impactAreas'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='Which of these impact areas are you most interested in?'
        description="These are based on Sustainable Development Goals (SDGs). You don't need to have experience in these areas, we use this to get a sense of what projects you may be interested in supporting."
      >
        <MultiSelectDropdown
          register={() => {
            return register('impactAreas', {
              required: 'Select at least one impact area to continue',
            });
          }}
          selectedItems={watch('impactAreas') || []}
          onSelectionChange={onSelectionChange}
          options={sdgOptions}
          placeholder='Select impact areas'
          label='Select as many as apply'
          error={errors.impactAreas?.message}
        />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
